import React, { useState, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import AddConnectionModal from '../../features/connections/AddConnectionModal';
import EditConnectionModal from '../../features/connections/EditConnectionModal';
import ConfirmationModal from '../../components/common/ConfirmationModal/ConfirmationModal';
import LoadingBackdrop from '../../components/common/LoadingBackdrop/LoadingBackdrop';
import EntityList from '../../components/common/EntityList/EntityList';
import { connectionService } from '../../services/apiService';
import useDebounce from '../../hooks/useDebounce';

function Connections() {
    const [connections, setConnections] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [cloneSourceConnection, setCloneSourceConnection] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const KEY = 'connectionsState';
    const parsedData = JSON.parse(sessionStorage.getItem(KEY) || '{}');

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(parsedData?.page || 1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchQuery, setSearchQuery] = useState(parsedData?.search || '');
    const itemsPerPage = 6;
    const debouncedSearch = useDebounce(searchQuery, 500);

    useEffect(() => {
        sessionStorage.setItem(KEY, JSON.stringify({ page: currentPage, search: searchQuery }));
    }, [currentPage, searchQuery]);

    // Fetch connections from API
    const fetchConnections = useCallback(async () => {
        // setIsLoading(true);
        try {
            // Delay of 300ms
            await new Promise((resolve) => { setTimeout(resolve, 300) });

            const response = await connectionService.getAllConnections({
                page: currentPage,
                limit: itemsPerPage,
                search: debouncedSearch
            });

            if (currentPage > 1 && response.items.length === 0) {
                setCurrentPage(prev => prev - 1);
            }

            setConnections(response.items || []);
            setTotalItems(response.total || 0);


            // console.log('Fetched connections:', response);
        } catch (error) {
            toast.error(error.message || 'Failed to fetch connections');
            console.error('Fetch connections error:', error);
            setConnections([]);
            setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch]);

    // Fetch connections on mount and when page/search changes
    useEffect(() => {
        setIsLoading(true);
        fetchConnections();
    }, [fetchConnections]);

    // Reset to page 1 when search query changes
    // useEffect(() => {
    //     if (currentPage !== 1) {
    //         setCurrentPage(1);
    //     }
    // }, [debouncedSearch]);

    const handleToggle = (id) => {
        const connection = connections.find(conn => conn.id === id);
        if (connection) {
            // Store the connection and action to perform after confirmation
            setPendingAction({
                type: 'toggle',
                id,
                action: connection.is_active ? 'deactivate' : 'activate'
            });
            setIsConfirmModalOpen(true);
        }
    };

    // const handleConfirmToggle = async () => {
    //     if (!pendingToggle) return;

    //     setIsLoading(true);

    //     try {
    //         let response;

    //         if (pendingToggle.action === 'activate') {
    //             // Call activate API
    //             response = await connectionService.activateConnection(pendingToggle.id);
    //             if (response) toast.success('Connection activated successfully');
    //         } else {
    //             // Call deactivate API
    //             response = await connectionService.deactivateConnection(pendingToggle.id);
    //             if (response) toast.success('Connection deactivated successfully');
    //         }

    //         // Refresh connections list after toggle
    //         await fetchConnections();
    //     } catch (error) {
    //         toast.error(error.message || `Failed to ${pendingToggle.action} connection. Please try again.`);
    //         console.error('Toggle connection error:', error);
    //     } finally {
    //         setIsLoading(false);
    //         setIsConfirmModalOpen(false);
    //         setPendingToggle(null);
    //     }
    // };

    // const handleCancelToggle = () => {
    //     setIsConfirmModalOpen(false);
    //     setPendingToggle(null);
    // };

    const handleAddConnection = () => setIsAddModalOpen(true);
    const handleCloseAddModal = () => setIsAddModalOpen(false);

    const handleEditConnection = (connection) => {
        setSelectedConnection(connection);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedConnection(null);
    };

    // --- Clone ---
    const handleCloneConnection = (connection) => {
        setCloneSourceConnection(connection);
        setIsCloneModalOpen(true);
    };

    const handleCloseCloneModal = () => {
        setIsCloneModalOpen(false);
        setCloneSourceConnection(null);
    };

    const handleDeleteConnection = (id) => {
        setPendingAction({ type: 'delete', id });
        setIsConfirmModalOpen(true);
    };

    const handleConfirm = async () => {
        if (!pendingAction) return;
        setIsLoading(true);

        try {
            if (pendingAction.type === 'toggle') {
                if (pendingAction.action === 'activate') {
                    const response = await connectionService.activateConnection(pendingAction.id);
                    if (response) toast.success('Connection activated successfully');
                } else {
                    const response = await connectionService.deactivateConnection(pendingAction.id);
                    if (response) toast.success('Connection deactivated successfully');
                }
            } else if (pendingAction.type === 'delete') {
                const response = await connectionService.deleteConnection(pendingAction.id);
                if (response) toast.success('Connection deleted successfully');
            }

            await fetchConnections();
        } catch (error) {
            const fallback = pendingAction.type === 'delete'
                ? 'Failed to delete connection. Please try again.'
                : `Failed to ${pendingAction.action} connection. Please try again.`;
            toast.error(error.message || fallback);
            console.error('Confirm action error:', error);
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setPendingAction(null);
        }
    };

    const handleCancelConfirm = () => {
        setIsConfirmModalOpen(false);
        setPendingAction(null);
    };

    const handlePageChange = (newPage) => setCurrentPage(newPage);

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    // Callback to refresh connections after successful add/update
    const handleConnectionAdded = async () => {
        if (currentPage === 1) {
            await fetchConnections();
        } else {
            setCurrentPage(1);
        }
    }
    const handleConnectionUpdated = () => fetchConnections();

    const confirmMessage = pendingAction?.type === 'delete'
        ? 'Do you want to delete this connection?'
        : 'Do you want to proceed?';

    return (
        <div className="page-content">
            {/* Loading Backdrop */}
            <LoadingBackdrop isLoading={isLoading} />

            <div className="page-header">
                <div className="page-title-section">
                    <h1 className="page-title">Database Connections</h1>
                    <p className="page-subtitle">Browse and manage all connections</p>
                </div>
                <button className="add-connection-btn" onClick={handleAddConnection}>
                    <Plus style={{ width: "1.042vw", height: "1.042vw" }} />
                    Add New Connection
                </button>
            </div>

            <EntityList
                items={connections}
                title="Connections"
                searchPlaceholder="Search connections..."
                itemsPerPage={itemsPerPage}
                itemName="connections"
                onToggle={handleToggle}
                onAdd={handleAddConnection}
                onEdit={handleEditConnection}
                onClone={handleCloneConnection}
                onDelete={handleDeleteConnection}
                // Server-side pagination
                currentPage={currentPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                // Server-side search
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
            />

            {/* Clone Connection Modal */}
            {isCloneModalOpen && (
                <AddConnectionModal
                    isOpen={isCloneModalOpen}
                    onClose={handleCloseCloneModal}
                    onSuccess={handleConnectionAdded}
                    setIsLoading={setIsLoading}
                    prefillData={cloneSourceConnection}
                />
            )}

            {/* Add Connection Modal */}
            {isAddModalOpen && (
                <AddConnectionModal
                    isOpen={isAddModalOpen}
                    onClose={handleCloseAddModal}
                    onSuccess={handleConnectionAdded}
                    setIsLoading={setIsLoading}
                />
            )}

            {/* Edit Connection Modal */}
            {isEditModalOpen && (
                <EditConnectionModal
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    connectionData={selectedConnection}
                    onSuccess={handleConnectionUpdated}
                    setIsLoading={setIsLoading}
                />
            )}

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCancelConfirm}
                onConfirm={handleConfirm}
                message={confirmMessage}
                title="Confirm Action"
                confirmText="Confirm"
                cancelText="Cancel"
                isLoading={isLoading}
            />
        </div>
    );
}

export default Connections;